<template>
  <VForm id="form-user-single-absence-details"
         :initial-values="initialFormValues"
         @submit="saveAbsence()"
  >
    <sdui-alert
      v-if="showAbsenceAlert"
      :content="$t('classbook.absences.user_absences.unhandled_absence_alert', { name: selectedUserDisplayName })"
      data-cy="unexcused-absence-alert"
      variant="warning"
      custom-icon="icon-info-o"
      data-test="unexcused-absence-alert"
      class="mb-2"
    >
      <template #title>
        <h4 class="mb-2">{{ $t('classbook.absences.user_absences.unhandled_absence_alert_title') }}</h4>
      </template>
    </sdui-alert>

    <slot name="afterHeader" />

    <slot name="datePickers">
      <div class="w-100 mb-4">
        <SDateRangePicker
          v-model="localAbsence"
          :from-title="$t('classbook.absences.user_absences.from')"
          :to-title="$t('classbook.absences.user_absences.till')"
          :max-date="maxDate"
          :min-date="minDate"
          :is-range-enforced="false"
          :disabled="isDisabled"
          :disable-begins-at="isBeginsAtDisabled"
          data-test="date-range-picker-stub"
          @update:model-value="handleDateRangeUpdate"
        />
      </div>
    </slot>
    <slot name="afterDatePickers">
      <sdui-alert
        v-if="filteredUserAbsenceInDateRange.length"
        :content="$t('sick_notes.create.absence_exists_in_range')"
        data-cy="overlapping-date-range"
        data-test="overlapping-date-range"
        variant="danger"
        custom-icon="icon-info"
        class="mb-2"
      />
      <sdui-alert
        v-if="isAbsenceTypeSelected"
        :content="$t('sick_notes.create.absence_type_missing')"
        variant="danger"
        custom-icon="icon-info"
        class="mb-2"
      />
    </slot>

    <!-- category -->
    <b-container class="px-0">
      <absence-category-display
        :absence="absence"
        :types="absenceTypes"
        :selected-type="selectedAbsenceType"
        :disabled="isDisabled"
        :default-type-category="defaultTypeCategory"
        data-cy="edit-absence-category"
        @click="showAbsenceCategoryModal = true"
      />
    </b-container>

    <b-container class="pt-2 pb-1 px-0">
      <!-- Parent Comment -->
      <sdui-comment-box
        v-if="showParentComment"
        v-model="localAbsence.excuse_note"
        aria-labelledby="excuse-note-description"
        :title="$t('classbook.absences.user_absences.excuse_note')"
        :disabled="!canUpdateParentNote"
        :placeholder="$t('classbook.absences.user_absences.excuse_note_placeholder')"
        data-test="parent-comment-box"
        class="mb-4"
      >
        <template #beforeText>
          <small v-if="absence.uuid"
                 id="excuse-note-description"
                 class="gray-300 mb-1"
          >{{ $t('sick_notes.edit.created_by', { user: authorName, dateTime: $dateFormat.date_time(localAbsence.created_at) }) }}
          </small>
        </template>
      </sdui-comment-box>

      <b-button
        v-if="showCreateTeacherComment && !showCommentBox"
        class="with-icon pl-0"
        variant="link"
        size="lg"
        data-cy="add-comment-btn"
        data-test="add-internal-comment-btn"
        @click="displayInternalCommentTextArea"
      >
        <sdui-icon class="icon-add-o mr-3"
                   variant="primary"
                   bg-variant="primary"
        />
        {{ $t('classbook.absences.user_absences.add_internal_comment') }}
      </b-button>

      <!-- Teacher Comment -->
      <sdui-comment-box
        v-if="showTeacherComment"
        ref="internalCommentTextArea"
        v-model="localAbsence.comment"
        :title="$t('classbook.absences.user_absences.teacher_comment')"
        :placeholder="$t('classbook.absences.user_absences.comment_placeholder')"
        class="mt-4"
        :disabled="!canUpdateTeacherComment"
        data-test="teacher-comment-box"
      >
        <template #icon>
          <STooltip placement="top" :title="$t('classbook.absences.user_absences.internal_comment')">
            <template #tooltip-trigger="{ triggerAttrs }">
              <b-button v-bind="triggerAttrs"
                        variant="link"
                        class="p-0 ml-2 mh-0"
                        :aria-label="$t('global.warning.title')"
              >
                <sdui-icon class="icon-info icon-16 text-muted" />
              </b-button>
            </template>
          </STooltip>
        </template>
      </sdui-comment-box>
    </b-container>

    <SModalPortalTo>
      <div class="w-100">
        <p v-if="infoText.length"
           class="gray-300 nano text-center mb-0"
           :class="{ 'mt-n2 mb-2': infoText.length }"
           data-cy="info-text"
        >
          {{ infoText }}
        </p>

        <!--eslint-disable-next-line sdui/require-focus-restore-on-disabled-button-->
        <b-button
          v-if="!isDisabled"
          variant="primary"
          type="submit"
          form="form-user-single-absence-details"
          size="lg w-100"
          data-cy="save-absence"
          data-test="save-absence-btn"
          :disabled="$wait.waiting('save-absence')"
        >
          <span v-if="absence.uuid">{{ submitBtnTitleComputed.update || $t('classbook.absences.user_absences.update_btn') }}</span>
          <span v-else>{{ submitBtnTitleComputed.add || $t('classbook.absences.user_absences.add_btn') }}</span>
        </b-button>
      </div>
    </SModalPortalTo>

    <SModal v-model="showAbsenceCategoryModal"
            hide-footer
            :header-title="$t('management.abscence_categories.modal.title')"
    >
      <absence-category-selection
        v-if="showAbsenceCategoryModal"
        :absence-types="absenceTypes.types"
        :absence-categories="absenceCategories"
        :is-selection-mode="true"
        :show-groups="absenceTypes && absenceTypes.meta && absenceTypes.meta.show_groups"
        @absence-type-selected="updateLocalAbsenceType"
      />
    </SModal>
  </VForm>
</template>

<script>
import { waitFor } from 'src/config/setup/plugins/waitFor/index.js'
import { Absence } from 'src/store/models/timetable/index.js'
import SDateRangePicker from 'src/components/base/datepicker/SDateRangePicker.vue'
import SduiCommentBox from 'src/components/miscellaneous/ui/comments/SduiCommentBox.vue'
import isMobileDeviceMixin from 'src/mixins/miscellaneous/isMobileDeviceMixin.js'
import SduiAlert from 'src/components/miscellaneous/ui/common/SduiAlert.vue'
import AbsenceCategorySelection from 'src/components/classbook/absences/AbsenceCategorySelection.vue'
import AbsenceCategoryDisplay from 'src/components/classbook/absences/AbsenceCategoryDisplay.vue'
import absenceCategories from 'src/mixins/classbook/absenceCategories.js'
import constants from 'src/config/constants.ts'
import SModalPortalTo from 'src/components/base/SModal/SModalPortalTo.vue'
import Classbooks from 'src/api/classbooks.js'
import STooltip from 'src/components/base/STooltip.vue'
import clyTracking from 'src/config/services/countlyTracking/clyTracking.js'

export default {
  name: 'UserSingleAbsenceDetails',

  inject: {
    translations: {
      default: {},
    },
  },

  components: {
    AbsenceCategoryDisplay,
    AbsenceCategorySelection,
    SduiCommentBox,
    SDateRangePicker,
    SduiAlert,
    SModalPortalTo,
    STooltip,
  },

  mixins: [isMobileDeviceMixin, absenceCategories],

  props: {
    fullDayAbsence: {
      type: Boolean,
      default: true,
    },

    absence: {
      type: Object,
      default: () => {},
    },

    currentSchoolYearDates: {
      type: Object,
      default: () => ({}),
    },

    classbook: {
      type: Number,
      required: true,
    },

    selectedUser: {
      type: Object,
      default: null,
    },

    can: {
      type: Object,
      default: null,
    },

    entity: {
      type: String,
      default: null,
    },

    submitBtnTitle: {
      type: Object,
      default: null,
    },

    ignoreDeleted: {
      type: Boolean,
      default: false,
    },

    defaultTypeCategory: {
      type: String,
      default: constants.classbook.absences.types.STANDARD_TYPE_CATEGORY,
    },
  },

  data: () => ({
    comment: '',
    localAbsence: {},
    showCommentBox: false,
    isAbsenceTypeSelected: false,
    userAbsencesInDateRange: [],
    lastCheckedDateRange: {
      begins_at: null,
      ends_at: null,
    },
  }),

  computed: {
    initialFormValues() {
      return {
        begins_at: this.localAbsence?.begins_at,
        ends_at: this.localAbsence?.ends_at,
      }
    },

    submitBtnTitleComputed() {
      return this.submitBtnTitle || this.translations?.submitBtn || {}
    },

    hasPendingChanges() {
      if (!this.absence || !this.localAbsence) return false

      const removeNullish = (obj) => Object.entries(obj).reduce((acc, [k, v]) => (v ? { ...acc, [k]: v } : acc), {})

      // remove nullish values, makes comparison easier
      const absence = removeNullish(this.absence)
      const localAbsence = removeNullish(this.localAbsence)

      // if absence is getting created, the dateRange is pre-selected. We should only return true if the date has
      // changed from the pre-selected date
      if (!this.absence.uuid) {
        return !this.$compare({ ...absence, ...this.getCorrectDate() }, localAbsence)
      }
      return !this.$compare(absence, localAbsence)
    },

    isParent() {
      return this.$me?.childs.length
    },

    infoText() {
      if (this.localAbsence.deleted_at)
        return this.$t('classbook.absences.user_absences.deleted_by', { user: this.editedByName, dateTime: this.deletedAt })
      if (!this.isUpdated && this.localAbsence.created_at)
        return this.$t('classbook.absences.user_absences.created_by', { user: this.authorName, dateTime: this.updatedCreatedAt })
      if (this.isUpdated)
        return this.$t('classbook.absences.user_absences.updated_by', { user: this.editedByName, dateTime: this.updatedCreatedAt })

      return ''
    },

    showParentComment() {
      if (!this.absence.uuid) return this.$can('create-absences', this.can)

      if (!this.$can('view-absence-parent-note', this.absence)) return false

      if (this.$can('update-absence-parent-note', this.absence)) return true

      return this.absence.excuse_note?.length
    },

    showCreateTeacherComment() {
      if (this.isDisabled) return false
      if (this.absence.comment?.length) return false

      return this.$can('create-absence-comment', this.can)
    },

    canUpdateTeacherComment() {
      if (this.absence.uuid) return this.$can('update-absence-comment', this.absence)

      return this.$can('create-absence-comment', this.can)
    },

    canUpdateParentNote() {
      if (this.absence.uuid) return this.$can('update-absence-parent-note', this.absence)
      return this.$can('create-absences', this.can)
    },

    showTeacherComment() {
      if (this.showCommentBox) return true

      if (!this.absence.uuid || !this.$can('view-absence-comment', this.absence)) return false

      return !!this.absence.comment?.length
    },

    isUpdated() {
      return this.localAbsence.meta?.last_updated_at
    },

    correctEntity() {
      const entity = this.entity || this.$route.params.entity
      if (entity === 'grades') return 'grade_id'
      if (entity === 'courses') return 'course_id'
      return 'user_id'
    },

    authorName() {
      return this.localAbsence.author?.meta?.displayname
    },

    editedByName() {
      return this.localAbsence.edited_by?.meta?.displayname
    },

    updatedCreatedAt() {
      if (this.isAbsenceUpdated) return this.$dateFormat.date_time(this.localAbsence.updated_at)
      return this.$dateFormat.date_time(this.localAbsence.created_at)
    },

    deletedAt() {
      return this.$dateFormat.date_time(this.localAbsence.deleted_at)
    },

    isAbsenceUpdated() {
      return this.localAbsence.read_at && this.$moment(this.localAbsence.read_at).isBefore(this.localAbsence.updated_at)
    },

    minDate() {
      const canChangePast = !this.isParent || (this.absence.uuid && this.$can('change-past', this.absence))
      if (canChangePast) return this.currentSchoolYearDates?.begins_at
      return this.$dateFormat.timestamp(this.$moment().startOf('day'))
    },

    maxDate() {
      return this.$dateFormat.timestamp(this.$moment(this.currentSchoolYearDates?.ends_at).endOf('day'))
    },

    isDisabled() {
      if (this.absence.uuid) return !this.$can('update', this.absence)

      return false
    },

    showAbsenceAlert() {
      return this.absence.uuid && this.absence.type?.category === 'missing' && !this.$can('change-past', this.absence)
    },

    isBeginsAtDisabled() {
      if (!this.absence.uuid) return false
      return !this.$can('change-past', this.absence)
    },

    correctAbsenceType() {
      if (!Object.keys(this.absenceTypes).length) return
      if (!this.selectedAbsenceType) return this.absenceTypes.types.find((el) => el.category === this.defaultTypeCategory)
      return this.selectedAbsenceType
    },

    filteredUserAbsenceInDateRange() {
      if (this.localAbsence?.uuid) return this.userAbsencesInDateRange.filter((el) => el.uuid !== this.localAbsence.uuid)
      return this.userAbsencesInDateRange
    },

    selectedUserDisplayName () {
      return this.selectedUser.meta?.displayname
    },

    entityName() {
      return this.entity || 'users'
    },
  },

  emits: ['update', 'has-pending-changes'],

  methods: {
    async displayInternalCommentTextArea() {
      this.showCommentBox = true

      // when user clicks button to add an internal comment, we move the focus to the textarea
      await this.$nextTick()
      const internalCommentTextArea = this.$refs?.internalCommentTextArea?.$el?.querySelector('textarea')
      internalCommentTextArea?.focus()
    },

    async handleDateRangeUpdate({ begins_at, ends_at }) {
      // one of the dates is empty or begins_at is after ends_at
      if (!begins_at || !ends_at || this.$moment(begins_at).isAfter(ends_at)) return

      // dates are unchanged (TODO: it would be better to do this in SDatePicker)
      if (
        this.$moment(begins_at).isSame(this.lastCheckedDateRange.begins_at) &&
        this.$moment(ends_at).isSame(this.lastCheckedDateRange.ends_at)
      )
        return

      this.lastCheckedDateRange = { begins_at, ends_at }
      await this.fetchUserAbsencesInSameDateRange()
    },

    fetchUserAbsencesInSameDateRange: waitFor('fetch-user-absences-in-same-date-range', async function () {
      if (!this.selectedUser || this.isDisabled) return
      // Caution: We need the limit of 2 here because this API also returns the absence which may currently be edited
      const params = {
        with_deleted: 0,
        limit: 2,
        begins_at: this.localAbsence.begins_at,
        ends_at: this.localAbsence.ends_at,
      }
      const res = await Classbooks.getAbsencesByEntityNameEntityId(this.classbook, this.entityName, this.selectedUser.id, { params })

      this.userAbsencesInDateRange = res.data?.absences || []
    }),

    getCorrectDate() {
      if (!this.fullDayAbsence || this.absence.uuid) {
        return {
          begins_at: this.absence.begins_at,
          ends_at: this.absence.ends_at,
        }
      }

      /**
       * begins_at/ends_at exist => select these dates (editing existing absence)
       * [ELSE] TODAY in min/max dateRange => pre-select todays date (create new absence)
       * [ELSE] => pre-select minDate (which is schoolyear.begins_at in this context)
       */
      const isTodayInDateRange = this.$moment().isSameOrAfter(this.minDate) || this.$moment().isSameOrBefore(this.maxDate)
      const beginsAt = this.absence.begins_at || isTodayInDateRange ? this.$moment() : this.minDate
      const endsAt = this.absence.ends_at || isTodayInDateRange ? this.$moment() : this.minDate
      return {
        begins_at: this.$dateFormat.timestamp(this.$moment(beginsAt).startOf('day')),
        ends_at: this.$dateFormat.timestamp(this.$moment(endsAt).endOf('day')),
      }
    },

    validate() {
      // we dont want to show all errors directly, show them fix by fix (figma)
      this.isAbsenceTypeSelected = !this.correctAbsenceType
      return !this.isAbsenceTypeSelected
    },

    saveAbsence: waitFor('save-absence', async function () {
      const isEditMode = !!this.localAbsence.uuid
      if (!this.validate()) {
        clyTracking.trackClassbook('absence', 'FAILED', 'validation').end.save({ isEditMode })
        return
      }

      const data = {
        begins_at: this.localAbsence.begins_at,
        ends_at: this.localAbsence.ends_at,
        type_id: this.selectedAbsenceType?.id,
        [this.correctEntity]: this.selectedUser?.id || this.$route.params?.id,
        comment: this.localAbsence.comment || null,
        excuse_note: this.localAbsence.excuse_note || null,
      }

      try {
        const res = await Absence.saveAbsence(this.localAbsence.classbook_id, this.localAbsence.uuid, data, !!this.localAbsence.uuid)
        clyTracking.trackClassbook('absence').end.save({ isEditMode, trackGlobal: { feature: 'absence' } })
        this.$emit('update', res.data)
      } catch (e) {
        clyTracking.trackClassbook('absence', 'FAILED', e.message).end.save({ isEditMode })
        throw e
      }
    }),

    updateLocalAbsenceType(absenceType) {
      this.selectAbsenceType(absenceType)
      this.localAbsence = {
        ...this.localAbsence,
        type: absenceType,
      }
    },
  },

  watch: {
    absence: {
      handler: async function (newValue) {
        this.localAbsence = { ...newValue, ...this.getCorrectDate(), classbook_id: this.classbook }
        if (!this.selectedUser) return
        this.selectDefaultType()
      },

      immediate: true,
    },

    localAbsence: {
      handler: async function (newValue) {
        this.selectedAbsenceType = newValue?.type || this.correctAbsenceType
        this.$emit('has-pending-changes', this.hasPendingChanges)
      },

      deep: true,
    },
  },
}
</script>

<style scoped lang="scss">
.f-bottom {
  bottom: env(safe-area-inset-bottom) !important;
}
</style>
